import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'

export type ElectronicSignatureMode = 'text' | 'qr' | 'text_and_qr' | 'drawn' | 'drawn_and_qr'

export interface PdfPlacement {
  pageNumber: number
  x: number
  y: number
  width: number
  height: number
}

export interface SignatureContent {
  /**
   * Contenido del stamp:
   *  - 'text': cursiva con `text`
   *  - 'qr': QR (codifica `qrDataUrl`)
   *  - 'drawn': imagen dibujada en `drawnImageDataUrl`
   * Para slot QR-only el caller usa type='qr'. Para slot firma decide
   * 'text' o 'drawn' segun lo que haya elegido el usuario.
   */
  type: 'text' | 'qr' | 'drawn'
  text?: string
  qrDataUrl?: string
  drawnImageDataUrl?: string
}

export interface StampedResult {
  stampedBytes: Uint8Array
  signedHash: string
  pageWidth: number
  pageHeight: number
}

function bufferToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

async function renderCursiveTextToPng(text: string, width: number, height: number): Promise<Uint8Array> {
  const dpi = 3
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * dpi)
  canvas.height = Math.round(height * dpi)
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = 'rgba(0,0,0,0)'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const fontSize = Math.round(canvas.height * 0.55)
  ctx.font = `${fontSize}px "Dancing Script", "Brush Script MT", cursive`
  ctx.fillStyle = '#0a2540'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, 4, canvas.height / 2)

  const dataUrl = canvas.toDataURL('image/png')
  const base64 = dataUrl.replace(/^data:image\/png;base64,/, '')
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0))
}

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '')
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0))
}

// Tamaño minimo del QR en puntos PDF para que un celular promedio
// lo escanee sin problema. Con URL corta (signwiz.app/v/<8chars>) ~22
// chars el QR queda en version 1-2 (21-25 modulos), ~21mm = 60pt es
// suficiente para escaneo confiable a 20-30cm de distancia.
export const MIN_QR_PT = 60

const URL_PREFIX = 'Firmado con SignWiz'

function shortenUrl(url: string): string {
  try {
    const u = new URL(url)
    return `${u.host.replace(/^www\./, '')}${u.pathname}`
  } catch {
    return url
  }
}

export async function stampOntoPdfAtCoords(
  pdfBytes: Uint8Array,
  placement: PdfPlacement,
  content: SignatureContent,
  signerMeta: { signerName?: string; timestamp: string; verificationUrl: string }
): Promise<StampedResult> {
  const pdfDoc = await PDFDocument.load(pdfBytes)
  const pages = pdfDoc.getPages()
  if (placement.pageNumber < 1 || placement.pageNumber > pages.length) {
    throw new Error(`Página ${placement.pageNumber} fuera de rango (1-${pages.length})`)
  }
  const page = pages[placement.pageNumber - 1]
  const { width: pageWidth, height: pageHeight } = page.getSize()

  const x = Math.max(0, Math.min(placement.x, pageWidth - placement.width))
  const y = Math.max(0, Math.min(placement.y, pageHeight - placement.height))
  const w = Math.min(placement.width, pageWidth - x)
  const h = Math.min(placement.height, pageHeight - y)

  const helv = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const helvBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
  const urlText = shortenUrl(signerMeta.verificationUrl)

  // Tamaño de fuente para footer (URL): 6-8pt segun altura disponible.
  // Letra chica pero legible (>=6pt es el minimo razonable).
  const urlFontSize = Math.max(6, Math.min(8, h * 0.085))
  const urlBlockHeight = urlFontSize * 2 + 4 // 2 lineas + padding

  // Trunca el texto con ellipsis si excede el ancho disponible. pdf-lib
  // drawText con maxWidth hace WRAP (multilinea), no truncado, asi que
  // calculamos manualmente.
  const truncateToWidth = (s: string, maxW: number, font: typeof helv, size: number): string => {
    if (font.widthOfTextAtSize(s, size) <= maxW) return s
    const ellipsis = '…'
    let cut = s
    while (cut.length > 4 && font.widthOfTextAtSize(cut + ellipsis, size) > maxW) {
      cut = cut.slice(0, -1)
    }
    return cut + ellipsis
  }

  const drawUrlBlock = (bx: number, by: number, bw: number) => {
    page.drawText(truncateToWidth(URL_PREFIX, bw, helvBold, urlFontSize), {
      x: bx,
      y: by + urlFontSize + 2,
      size: urlFontSize,
      font: helvBold,
      color: rgb(0.15, 0.4, 0.7),
    })
    page.drawText(truncateToWidth(urlText, bw, helv, urlFontSize), {
      x: bx,
      y: by + 2,
      size: urlFontSize,
      font: helv,
      color: rgb(0.4, 0.45, 0.55),
    })
  }

  // Cada stamp coloca UN elemento (texto cursivo, dibujo o QR) con URL
  // "Firmado con SignWiz" debajo. Para combinaciones (text+qr, drawn+qr)
  // el caller llama dos veces, una por cada slot, sobre el mismo PDF.
  if (content.type === 'text' && content.text) {
    const cursiveHeight = h - urlBlockHeight
    const sigPng = await renderCursiveTextToPng(content.text, w, cursiveHeight)
    const sigImage = await pdfDoc.embedPng(sigPng)
    page.drawImage(sigImage, { x, y: y + urlBlockHeight, width: w, height: cursiveHeight })
    drawUrlBlock(x + 2, y, w - 4)
  } else if (content.type === 'drawn' && content.drawnImageDataUrl) {
    // Firma dibujada: embebida como PNG. La imagen captura el trazo
    // tal como lo dibujo el usuario en canvas.
    const drawnHeight = h - urlBlockHeight
    const drawnBytes = dataUrlToBytes(content.drawnImageDataUrl)
    const drawnImage = await pdfDoc.embedPng(drawnBytes)
    page.drawImage(drawnImage, { x, y: y + urlBlockHeight, width: w, height: drawnHeight })
    drawUrlBlock(x + 2, y, w - 4)
  } else if (content.type === 'qr' && content.qrDataUrl) {
    const availableQrSize = Math.min(w, h - urlBlockHeight - 2)
    if (availableQrSize < MIN_QR_PT) {
      throw new Error(
        `Rectángulo demasiado chico para un QR escaneable. Necesitas al menos ${MIN_QR_PT} pt cuadrados disponibles para el QR. Redibujá uno más grande.`
      )
    }
    const qrBytes = dataUrlToBytes(content.qrDataUrl)
    const qrImage = await pdfDoc.embedPng(qrBytes)
    // QR alineado al borde izquierdo del rect (no centrado).
    page.drawImage(qrImage, {
      x: x,
      y: y + urlBlockHeight + 2,
      width: availableQrSize,
      height: availableQrSize,
    })
    // URL al ancho del rect entero (no del QR) para que 'Firmado con
    // SignWiz' entre en una linea aunque el QR sea chico. Left-aligned
    // igual que el QR, asi quedan visualmente conectados.
    drawUrlBlock(x, y, w - 2)
  }

  const stampedBytes = await pdfDoc.save()
  const hashBuffer = await crypto.subtle.digest(
    'SHA-256',
    stampedBytes.buffer.slice(stampedBytes.byteOffset, stampedBytes.byteOffset + stampedBytes.byteLength) as ArrayBuffer
  )
  const signedHash = bufferToHex(hashBuffer)

  return { stampedBytes, signedHash, pageWidth, pageHeight }
}

/**
 * Embed a signature image into a PDF at a fixed position.
 * Position 'left' = sender (bottom-left), 'right' = recipient (bottom-right).
 */
export async function embedSignatureInPdf(
  pdfBytes: Uint8Array,
  signatureBase64: string,
  signerName: string,
  position: 'left' | 'right'
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(pdfBytes)
  const pages = pdfDoc.getPages()
  const lastPage = pages[pages.length - 1]
  const { width } = lastPage.getSize()

  // Clean base64 (remove data URL prefix if present)
  const cleanBase64 = signatureBase64.replace(/^data:image\/\w+;base64,/, '')
  const sigBytes = Uint8Array.from(atob(cleanBase64), c => c.charCodeAt(0))

  // Try PNG first, fall back to JPG
  let sigImage
  try {
    sigImage = await pdfDoc.embedPng(sigBytes)
  } catch {
    sigImage = await pdfDoc.embedJpg(sigBytes)
  }

  const sigWidth = 180
  const sigHeight = 70
  const x = position === 'left' ? 50 : width - 50 - sigWidth
  const y = 80

  // Draw signature image
  lastPage.drawImage(sigImage, { x, y, width: sigWidth, height: sigHeight })

  // Draw line above signature
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
  lastPage.drawLine({
    start: { x, y: y - 2 },
    end: { x: x + sigWidth, y: y - 2 },
    thickness: 0.5,
    color: rgb(0.3, 0.3, 0.3),
  })

  // Name below line
  lastPage.drawText(signerName, {
    x,
    y: y - 14,
    size: 8,
    font,
    color: rgb(0.3, 0.3, 0.3),
  })

  // Date below name
  const dateStr = new Date().toLocaleDateString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  lastPage.drawText(`Fecha: ${dateStr}`, {
    x,
    y: y - 24,
    size: 7,
    font,
    color: rgb(0.5, 0.5, 0.5),
  })

  return pdfDoc.save()
}

/**
 * Convert a File to Uint8Array
 */
export async function fileToUint8Array(file: File): Promise<Uint8Array> {
  const buffer = await file.arrayBuffer()
  return new Uint8Array(buffer)
}

/**
 * Convert Uint8Array to base64 string
 */
export function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Convert base64 string to Uint8Array
 */
export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Create a downloadable blob URL from base64 PDF data
 */
export function createPdfBlobUrl(base64Data: string): string {
  const bytes = base64ToUint8Array(base64Data)
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' })
  return URL.createObjectURL(blob)
}

/**
 * Download a PDF from base64 data
 */
export function downloadPdf(base64Data: string, filename: string): void {
  const url = createPdfBlobUrl(base64Data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
